/**
 * Risk Assessment Export Module - PDF generation for risk assessment reports
 *
 * Provides WBC3-compliant risk assessment documentation including:
 * - Individual assessment reports with risk matrix
 * - Register summary with statistics
 * - High risk management reports
 */

// FT-497 FIX: Use the functional autoTable API (same fix as FT-468 drillExport).
//
// Root cause: the bundle can contain DUPLICATE jsPDF module copies (Webpack/CRA
// code-splitting). The side-effect import `import 'jspdf-autotable'` patches the
// prototype of ONE copy; createPDF() may construct a document from a DIFFERENT
// copy that was never patched, causing "pdf.autoTable is not a function" at
// runtime. The functional API — autoTable(pdf, options) — operates on the passed
// document object directly, regardless of which prototype was patched, making it
// immune to the duplicate-copy problem.
//
// jspdf-autotable v5+ ESM: export { autoTable, autoTable as default }
// → `import autoTable from 'jspdf-autotable'` imports the function directly.
import autoTable from 'jspdf-autotable';

import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDate,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

import {
  RISK_LEVELS,
  LIKELIHOOD_SCALE,
  SEVERITY_SCALE,
  CONTROL_HIERARCHY,
  ALARP_ZONES,
  REVIEW_FREQUENCIES,
  STATUS_OPTIONS,
  getRiskLevel,
  getAlarpZone
} from '../../modules/risk-assessment/config/moduleConfig';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get RGB color array for risk level
 * @param {string} riskLevel - Risk level key
 * @returns {number[]} RGB color array
 */
/**
 * Add a label:value pair with bold label
 * @param {jsPDF} pdf - PDF document
 * @param {string} label - Label text
 * @param {string} value - Value text
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} labelWidth - Width allocated for label (default 25mm)
 */
function addLabelValue(pdf, label, value, x, y, labelWidth = 25) {
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${label}:`, x, y);
  pdf.setFont('helvetica', 'normal');
  pdf.text(value || 'N/A', x + labelWidth, y);
}

function getRiskLevelColor(riskLevel) {
  const colors = {
    extreme: [239, 68, 68],    // red-500
    high: [249, 115, 22],      // orange-500
    medium: [234, 179, 8],     // yellow-500
    low: [34, 197, 94]         // green-500
  };
  return colors[riskLevel] || colors.low;
}

/**
 * Add risk level badge to PDF
 * @param {jsPDF} pdf - PDF document
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {string} riskLevel - Risk level key
 * @param {number} score - Risk score
 */
function addRiskLevelBadge(pdf, x, y, riskLevel, score) {
  const color = getRiskLevelColor(riskLevel);

  pdf.setFillColor(...color);
  pdf.roundedRect(x, y, 35, 7, 2, 2, 'F');

  pdf.setTextColor(255, 255, 255);
  setFont(pdf, 'small');
  pdf.text(`${riskLevel.toUpperCase()} (${score})`, x + 17.5, y + 5, { align: 'center' });
  pdf.setTextColor(0, 0, 0);
}

/**
 * Draw 5x5 risk matrix with positions marked
 * @param {jsPDF} pdf - PDF document
 * @param {number} startX - Starting X position
 * @param {number} startY - Starting Y position
 * @param {Object} options - Matrix options
 * @returns {number} New Y position after matrix
 */
function drawRiskMatrix(pdf, startX, startY, options = {}) {
  const {
    initialLikelihood = null,
    initialSeverity = null,
    residualLikelihood = null,
    residualSeverity = null,
    cellSize = 12,
    showLegend = true
  } = options;

  const { margin } = getPageDimensions(pdf);
  let currentY = startY;

  // Draw matrix grid (likelihood on Y-axis, severity on X-axis)
  // Likelihood 5 at top, 1 at bottom
  for (let l = 5; l >= 1; l--) {
    for (let s = 1; s <= 5; s++) {
      const score = l * s;
      const level = getRiskLevel(score);
      const color = getRiskLevelColor(level);

      const cellX = startX + (s - 1) * cellSize + 20; // 20mm offset for labels
      const cellY = currentY + (5 - l) * cellSize;

      // Fill cell with risk color
      pdf.setFillColor(...color);
      pdf.rect(cellX, cellY, cellSize, cellSize, 'F');

      // Draw cell border
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.3);
      pdf.rect(cellX, cellY, cellSize, cellSize, 'S');

      // Add score number
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.text(String(score), cellX + cellSize / 2, cellY + cellSize / 2 + 2, { align: 'center' });

      // Mark initial position with filled circle
      if (initialLikelihood === l && initialSeverity === s) {
        pdf.setFillColor(0, 0, 0);
        pdf.circle(cellX + cellSize / 2 - 2, cellY + cellSize / 2, 1.5, 'F');
      }

      // Mark residual position with open circle
      if (residualLikelihood === l && residualSeverity === s) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        pdf.circle(cellX + cellSize / 2 + 2, cellY + cellSize / 2, 1.5, 'S');
      }
    }
  }

  pdf.setTextColor(0, 0, 0);
  pdf.setDrawColor(0, 0, 0);

  // Add axis labels
  pdf.setFontSize(8);

  // Y-axis label (Likelihood)
  pdf.text('L', startX + 5, currentY + cellSize * 2.5, { align: 'center' });
  pdf.text('I', startX + 5, currentY + cellSize * 2.5 + 3, { align: 'center' });
  pdf.text('K', startX + 5, currentY + cellSize * 2.5 + 6, { align: 'center' });
  pdf.text('E', startX + 5, currentY + cellSize * 2.5 + 9, { align: 'center' });

  // Y-axis values
  for (let l = 5; l >= 1; l--) {
    pdf.text(String(l), startX + 15, currentY + (5 - l) * cellSize + cellSize / 2 + 2, { align: 'center' });
  }

  // X-axis label (Severity)
  pdf.text('SEVERITY', startX + 20 + cellSize * 2.5, currentY + cellSize * 5 + 8, { align: 'center' });

  // X-axis values
  for (let s = 1; s <= 5; s++) {
    pdf.text(String(s), startX + 20 + (s - 1) * cellSize + cellSize / 2, currentY + cellSize * 5 + 4, { align: 'center' });
  }

  // Add legend if requested
  if (showLegend) {
    const legendY = currentY + cellSize * 5 + 12;
    pdf.setFontSize(7);

    // Initial risk marker
    pdf.setFillColor(0, 0, 0);
    pdf.circle(startX + 22, legendY, 1.5, 'F');
    pdf.text('Initial Risk', startX + 26, legendY + 1);

    // Residual risk marker
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.circle(startX + 52, legendY, 1.5, 'S');
    pdf.text('Residual Risk', startX + 56, legendY + 1);

    return currentY + cellSize * 5 + 18;
  }

  return currentY + cellSize * 5 + 8;
}

/**
 * Add professional hazard table with dynamic row heights using jspdf-autotable
 * Automatically handles: row heights based on content, page breaks, text wrapping
 * NO TRUNCATION - all control measures shown in full (WBC3 compliance)
 *
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {Array} hazards - Array of hazard objects
 * @returns {number} New Y position after table
 */
function addHazardTable(pdf, currentY, hazards) {
  const { margin } = getPageDimensions(pdf);

  // Prepare table data - NO .slice() truncation
  const tableData = hazards.map((hazard, index) => {
    // Hazard column: description + who affected
    let hazardText = hazard.hazardDescription || 'Unnamed';
    if (hazard.whoAffected) {
      hazardText += `\n(Who: ${hazard.whoAffected})`;
    }

    // Initial risk column
    const initialScore = hazard.initialRiskScore || (hazard.initialLikelihood * hazard.initialSeverity) || 0;
    const initialLevel = getRiskLevel(initialScore);
    const initialText = `${hazard.initialLikelihood || '-'}×${hazard.initialSeverity || '-'}=${initialScore}\n${initialLevel.toUpperCase()}`;

    // Controls column: FULL TEXT - no truncation
    let controlsText = '';
    if (hazard.existingControls) {
      controlsText += 'EXISTING:\n• ' + hazard.existingControls;
    }
    if (hazard.additionalControls) {
      if (controlsText) controlsText += '\n\n';
      controlsText += 'ADDITIONAL:\n• ' + hazard.additionalControls;
    }
    if (!controlsText) {
      controlsText = 'None specified';
    }

    // Residual risk column
    const residualScore = hazard.residualRiskScore || (hazard.residualLikelihood * hazard.residualSeverity) || 0;
    const residualLevel = getRiskLevel(residualScore);
    const residualText = `${hazard.residualLikelihood || '-'}×${hazard.residualSeverity || '-'}=${residualScore}\n${residualLevel.toUpperCase()}`;

    // ALARP column
    const alarpZone = getAlarpZone(residualScore);
    const alarpInfo = ALARP_ZONES[alarpZone];
    const alarpText = alarpInfo?.label?.toUpperCase() || 'N/A';

    return [
      String(index + 1), // No.
      hazardText,        // Hazard
      initialText,       // Initial Risk
      controlsText,      // Controls (FULL TEXT)
      residualText,      // Residual Risk
      alarpText          // ALARP
    ];
  });

  // Render table with autotable - handles dynamic heights and page breaks
  // FT-497: functional API avoids duplicate-jsPDF-copy prototype issue
  autoTable(pdf, {
    startY: currentY,
    head: [['No', 'Hazard', 'Initial', 'Control Measures', 'Residual', 'ALARP']],
    body: tableData,
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      cellWidth: 'wrap',
      valign: 'top'
    },
    headStyles: {
      fillColor: [71, 85, 105], // slate-700
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left'
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },   // No
      1: { cellWidth: 40 },                    // Hazard
      2: { cellWidth: 22, halign: 'center' },  // Initial
      3: { cellWidth: 56 },                    // Controls (widest for full text)
      4: { cellWidth: 22, halign: 'center' },  // Residual
      5: { cellWidth: 12, halign: 'center', fontSize: 6 } // ALARP
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251] // gray-50
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      // Color-code risk level text in Initial and Residual columns
      if (data.section === 'body' && (data.column.index === 2 || data.column.index === 4)) {
        const cellText = data.cell.raw?.toLowerCase() || '';
        if (cellText.includes('extreme')) {
          data.cell.styles.textColor = [220, 38, 38]; // red-600
        } else if (cellText.includes('high')) {
          data.cell.styles.textColor = [234, 88, 12]; // orange-600
        } else if (cellText.includes('medium')) {
          data.cell.styles.textColor = [202, 138, 4]; // yellow-600
        } else if (cellText.includes('low')) {
          data.cell.styles.textColor = [22, 163, 74]; // green-600
        }
      }

      // Color-code ALARP column
      if (data.section === 'body' && data.column.index === 5) {
        const alarpText = data.cell.raw?.toLowerCase() || '';
        if (alarpText.includes('intolerable')) {
          data.cell.styles.textColor = [220, 38, 38]; // red-600
          data.cell.styles.fontStyle = 'bold';
        } else if (alarpText.includes('tolerable')) {
          data.cell.styles.textColor = [202, 138, 4]; // yellow-600
        } else if (alarpText.includes('broadly')) {
          data.cell.styles.textColor = [22, 163, 74]; // green-600
        }
      }
    }
  });

  // Return Y position after table
  return pdf.lastAutoTable.finalY + 4;
}

/**
 * Add compact risk matrix with 8mm cells (side-by-side layout component)
 * @param {jsPDF} pdf - PDF document
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} initialL - Initial likelihood
 * @param {number} initialS - Initial severity
 * @param {number} residualL - Residual likelihood
 * @param {number} residualS - Residual severity
 */
function addCompactRiskMatrix(pdf, x, y, initialL, initialS, residualL, residualS) {
  const cellSize = 8; // Reduced from 12mm to 8mm

  // Draw grid
  for (let l = 5; l >= 1; l--) {
    const rowY = y + (5 - l) * cellSize;

    for (let s = 1; s <= 5; s++) {
      const colX = x + (s - 1) * cellSize + 8; // 8mm offset for Y-axis labels
      const score = l * s;
      const level = getRiskLevel(score);
      const color = getRiskLevelColor(level);

      // Cell background color
      pdf.setFillColor(...color);
      pdf.rect(colX, rowY, cellSize, cellSize, 'F');

      // Cell border
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.2);
      pdf.rect(colX, rowY, cellSize, cellSize, 'S');

      // Score number
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(6);
      pdf.text(String(score), colX + cellSize/2, rowY + cellSize/2 + 1.5, { align: 'center' });

      // Mark initial position with filled circle
      if (l === Math.round(initialL) && s === Math.round(initialS)) {
        pdf.setFillColor(0, 0, 0);
        pdf.circle(colX + cellSize/2 - 1.5, rowY + cellSize/2 - 1, 1, 'F');
      }

      // Mark residual position with open circle
      if (l === Math.round(residualL) && s === Math.round(residualS)) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.4);
        pdf.circle(colX + cellSize/2 + 1.5, rowY + cellSize/2 + 1, 1, 'S');
      }
    }
  }

  pdf.setTextColor(0, 0, 0);
  pdf.setDrawColor(0, 0, 0);

  // Y-axis values (1-5)
  pdf.setFontSize(5);
  for (let l = 5; l >= 1; l--) {
    pdf.text(String(l), x + 4, y + (5 - l) * cellSize + cellSize/2 + 1, { align: 'center' });
  }

  // X-axis values (1-5)
  for (let s = 1; s <= 5; s++) {
    pdf.text(String(s), x + 8 + (s - 1) * cellSize + cellSize/2, y + cellSize * 5 + 3, { align: 'center' });
  }

  // Axis labels
  pdf.setFontSize(5);
  pdf.text('L', x + 1, y + cellSize * 2.5 + 1);
  pdf.text('SEVERITY', x + 8 + cellSize * 2.5, y + cellSize * 5 + 7, { align: 'center' });

  // Legend
  pdf.setFontSize(5);
  pdf.setFillColor(0, 0, 0);
  pdf.circle(x + 10, y + cellSize * 5 + 11, 1, 'F');
  pdf.text('Initial', x + 13, y + cellSize * 5 + 12);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.circle(x + 28, y + cellSize * 5 + 11, 1, 'S');
  pdf.text('Residual', x + 31, y + cellSize * 5 + 12);
}

/**
 * Add side-by-side risk summary and matrix (52% space saving)
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {Object} assessment - Assessment object
 * @returns {number} New Y position
 */
function addSideBySideRiskSummary(pdf, currentY, assessment) {
  const { margin, contentWidth } = getPageDimensions(pdf);
  const startY = currentY;

  // Split into two columns
  const col1Width = 85; // mm (summary)
  const col2Width = 70; // mm (matrix)
  const gutter = 5;     // mm gap

  const summaryX = margin;
  const matrixX = margin + col1Width + gutter;

  // Draw border around the whole section
  pdf.setDrawColor(226, 232, 240); // gray-200
  pdf.setLineWidth(0.3);
  pdf.rect(margin, currentY - 2, contentWidth, 52, 'S');

  // LEFT COLUMN: Risk Summary
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Risk Summary', summaryX + 3, currentY + 4);

  currentY += 10;
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');

  // Initial Risk
  const initialScore = assessment.initialRiskScore || 0;
  const initialLevel = getRiskLevel(initialScore);
  pdf.text('Initial Risk:', summaryX + 3, currentY);
  pdf.setFont('helvetica', 'bold');
  const initialColor = getRiskLevelColor(initialLevel);
  pdf.setTextColor(...initialColor);
  pdf.text(`${initialScore} (${initialLevel.toUpperCase()})`, summaryX + 25, currentY);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  currentY += 6;

  // Residual Risk
  const residualScore = assessment.residualRiskScore || 0;
  const residualLevel = getRiskLevel(residualScore);
  pdf.text('Residual Risk:', summaryX + 3, currentY);
  pdf.setFont('helvetica', 'bold');
  const residualColor = getRiskLevelColor(residualLevel);
  pdf.setTextColor(...residualColor);
  pdf.text(`${residualScore} (${residualLevel.toUpperCase()})`, summaryX + 25, currentY);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  currentY += 6;

  // Risk Reduction
  const reduction = initialScore > 0 ? initialScore - residualScore : 0;
  const percentage = initialScore > 0 ? Math.round((reduction / initialScore) * 100) : 0;
  pdf.text('Risk Reduction:', summaryX + 3, currentY);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(21, 128, 61); // green-600
  pdf.text(`${percentage}% (${reduction} point drop)`, summaryX + 25, currentY);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  currentY += 6;

  // ALARP Status
  const alarpZone = getAlarpZone(residualScore);
  const alarpInfo = ALARP_ZONES[alarpZone];
  pdf.text('ALARP Status:', summaryX + 3, currentY);
  pdf.setFont('helvetica', 'bold');
  pdf.text(alarpInfo?.label || 'N/A', summaryX + 25, currentY);
  pdf.setFont('helvetica', 'normal');
  currentY += 4;
  pdf.setFontSize(6);
  pdf.setTextColor(107, 114, 128); // gray-500
  pdf.text('(As Low As Reasonably Practicable)', summaryX + 3, currentY);
  pdf.setTextColor(0, 0, 0);

  // RIGHT COLUMN: Compact Risk Matrix
  // Get average L/S from hazards or use assessment values
  let avgInitialL = 3, avgInitialS = 3, avgResidualL = 2, avgResidualS = 2;

  if (assessment.hazards && assessment.hazards.length > 0) {
    const hazard = assessment.hazards[0]; // Use first hazard for matrix
    avgInitialL = hazard.initialLikelihood || 3;
    avgInitialS = hazard.initialSeverity || 3;
    avgResidualL = hazard.residualLikelihood || 2;
    avgResidualS = hazard.residualSeverity || 2;
  }

  addCompactRiskMatrix(pdf, matrixX, startY + 2, avgInitialL, avgInitialS, avgResidualL, avgResidualS);

  return startY + 54; // Fixed height for side-by-side layout
}

/**
 * Get WBC3 reference from assessment
 * @param {Object} assessment - Risk assessment object
 * @returns {Object|null} WBC3 reference info
 */
function getWBC3Reference(assessment) {
  if (assessment.selectedTask?.wbc3Reference) {
    return {
      taskId: assessment.selectedTask.id,
      taskTitle: assessment.selectedTask.title,
      reference: assessment.selectedTask.wbc3Reference
    };
  }
  return null;
}

/**
 * Calculate register statistics
 * @param {Array} assessments - Array of assessments
 * @returns {Object} Statistics object
 */
function calculateRegisterStatistics(assessments) {
  const stats = {
    total: assessments.length,
    byRiskLevel: {
      extreme: 0,
      high: 0,
      medium: 0,
      low: 0
    },
    byStatus: {
      draft: 0,
      underReview: 0,
      approved: 0,
      active: 0,
      archived: 0
    },
    overdue: 0,
    dueSoon: 0,
    averageRiskReduction: 0
  };

  let totalReduction = 0;
  let hazardCount = 0;

  assessments.forEach(a => {
    // By risk level
    const level = a.riskLevel || getRiskLevel(a.residualRiskScore || 0);
    if (stats.byRiskLevel[level] !== undefined) {
      stats.byRiskLevel[level]++;
    }

    // By status
    const status = a.status || 'draft';
    if (status === 'under_review') {
      stats.byStatus.underReview++;
    } else if (stats.byStatus[status] !== undefined) {
      stats.byStatus[status]++;
    }

    // Overdue check
    if (a.nextReviewDate && new Date(a.nextReviewDate) < new Date()) {
      stats.overdue++;
    } else if (a.nextReviewDate) {
      const daysUntilDue = Math.ceil((new Date(a.nextReviewDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue <= 30) {
        stats.dueSoon++;
      }
    }

    // Average risk reduction
    if (a.hazards && a.hazards.length > 0) {
      a.hazards.forEach(h => {
        const initial = h.initialRiskScore || (h.initialLikelihood * h.initialSeverity) || 0;
        const residual = h.residualRiskScore || (h.residualLikelihood * h.residualSeverity) || 0;
        if (initial > 0) {
          totalReduction += ((initial - residual) / initial) * 100;
          hazardCount++;
        }
      });
    }
  });

  stats.averageRiskReduction = hazardCount > 0 ? Math.round(totalReduction / hazardCount) : 0;

  return stats;
}

// =============================================================================
// INDIVIDUAL ASSESSMENT EXPORT
// =============================================================================

/**
 * Export individual risk assessment as detailed PDF
 * @param {Object} assessment - Risk assessment object
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIndividualRiskAssessmentPDF(assessment, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      vesselSettings = {},  // Contains officialNumber, grossTonnage for WBC3 workboats
      companyName = '',
      includeRiskMatrix = true,
      includeControlHierarchy = true,
      includeSignatures = true
    } = options;

    if (!assessment) {
      alert('No assessment data provided for export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth, pageHeight } = getPageDimensions(pdf);

    let currentY = margin;

    // === DOCUMENT HEADER BAR ===
    const headerHeight = 20;

    // Dark header bar
    pdf.setFillColor(30, 41, 59);  // slate-800
    pdf.rect(margin, currentY, contentWidth, headerHeight, 'F');

    // Header text in white
    pdf.setTextColor(255, 255, 255);

    // Line 1: RA Title (prominent)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    const raTitle = `${(assessment.title || 'UNTITLED').toUpperCase()} RISK ASSESSMENT`;
    pdf.text(raTitle, margin + 5, currentY + 7);

    // Line 2: Vessel & Company (secondary)
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const subLine = [
      vesselName || 'Unknown Vessel',
      companyName || ''
    ].filter(Boolean).join(' | ');
    pdf.text(subLine, margin + 5, currentY + 14);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    currentY += headerHeight + 4;

    // Assessment Details
    currentY = addSectionHeading(pdf, currentY, 'Assessment Details', 'heading');

    setFont(pdf, 'subheading');
    pdf.text(assessment.title || 'Untitled Assessment', margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 2;

    setFont(pdf, 'body');
    addLabelValue(pdf, 'ID', assessment.id, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    addLabelValue(pdf, 'Type', assessment.type?.replace(/_/g, ' ') || 'generic', margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    addLabelValue(pdf, 'Status', STATUS_OPTIONS[assessment.status]?.label || assessment.status || 'Draft', margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    if (assessment.location) {
      addLabelValue(pdf, 'Location', assessment.location, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    if (assessment.department) {
      addLabelValue(pdf, 'Department', assessment.department, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    addLabelValue(pdf, 'Created', formatDate(assessment.createdAt), margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    if (assessment.description) {
      currentY += 3;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Description:', margin, currentY);
      pdf.setFont('helvetica', 'normal');
      currentY += PDF_CONFIG.lineHeight;
      currentY = addWrappedText(pdf, currentY, assessment.description, { indent: 0 });
    }

    // Related Procedures (optional)
    if (assessment.procedureNotes) {
      currentY += 3;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Related Procedures:', margin, currentY);
      pdf.setFont('helvetica', 'normal');
      currentY += PDF_CONFIG.lineHeight;
      currentY = addWrappedText(pdf, currentY, assessment.procedureNotes, { indent: 0 });
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // Risk Summary + Matrix (Side-by-Side - 52% space saving)
    if (includeRiskMatrix) {
      currentY = addSideBySideRiskSummary(pdf, currentY, assessment);
      currentY += PDF_CONFIG.sectionSpacing;
    } else {
      // Fallback to text-only summary if matrix disabled
      currentY = addSectionHeading(pdf, currentY, 'Risk Summary', 'heading');
      const initialScore = assessment.initialRiskScore || 0;
      const residualScore = assessment.residualRiskScore || 0;
      setFont(pdf, 'body');
      pdf.text(`Initial Risk: ${initialScore} (${getRiskLevel(initialScore).toUpperCase()})`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
      pdf.text(`Residual Risk: ${residualScore} (${getRiskLevel(residualScore).toUpperCase()})`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
      currentY += PDF_CONFIG.sectionSpacing;
    }

    // Hazards Table (78% space saving vs text blocks)
    if (assessment.hazards && assessment.hazards.length > 0) {
      currentY = addSectionHeading(pdf, currentY, `Identified Hazards (${assessment.hazards.length})`, 'heading');

      // Check if we need a page break for the table
      const tableHeight = 7 + (assessment.hazards.length * 16); // Header + rows
      const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: 'Risk Assessment Report (continued)',
        vesselName,
        category
      }), tableHeight > 150 ? 50 : tableHeight);
      currentY = pageBreak.currentY;

      currentY = addHazardTable(pdf, currentY, assessment.hazards);
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // Review Schedule
    const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: 'Risk Assessment Report (continued)',
      vesselName,
      category
    }), 40);
    currentY = pageBreak.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Review Schedule', 'heading');

    setFont(pdf, 'body');
    const reviewFreq = REVIEW_FREQUENCIES[assessment.reviewFrequency];
    pdf.text(`Review Frequency: ${reviewFreq?.label || 'Annually'}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    if (assessment.nextReviewDate) {
      const isOverdue = new Date(assessment.nextReviewDate) < new Date();
      pdf.text(`Next Review Due: ${formatDate(assessment.nextReviewDate)}${isOverdue ? ' (OVERDUE)' : ''}`, margin, currentY);
      if (isOverdue) {
        pdf.setTextColor(239, 68, 68);
        pdf.text(' (OVERDUE)', margin + 45, currentY);
        pdf.setTextColor(0, 0, 0);
      }
      currentY += PDF_CONFIG.lineHeight;
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // WBC3 Compliance
    const wbc3Ref = getWBC3Reference(assessment);
    currentY = addSectionHeading(pdf, currentY, 'Regulatory Compliance', 'heading');

    // Regulatory Framework header
    setFont(pdf, 'subheading');
    pdf.text('Regulatory Framework:', margin, currentY);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Workboat Code Edition 3 (WBC3)', margin + 40, currentY);
    pdf.setFont('helvetica', 'normal');
    currentY += PDF_CONFIG.lineHeight + 2;

    // Compliance checklist
    setFont(pdf, 'body');
    pdf.setFillColor(34, 197, 94); // green-500
    pdf.circle(margin + 3, currentY - 1.5, 1.5, 'F');
    pdf.text('WBC3 Section 3.14: Risk Assessment of Operations', margin + 8, currentY);
    currentY += PDF_CONFIG.lineHeight;

    pdf.setFillColor(34, 197, 94);
    pdf.circle(margin + 3, currentY - 1.5, 1.5, 'F');
    pdf.text('WBC3 Section 31: Safety Management', margin + 8, currentY);
    currentY += PDF_CONFIG.lineHeight;

    pdf.setFillColor(34, 197, 94);
    pdf.circle(margin + 3, currentY - 1.5, 1.5, 'F');
    pdf.text('WBC3 Appendix 8: SMS Requirements', margin + 8, currentY);
    currentY += PDF_CONFIG.lineHeight;

    pdf.setFillColor(34, 197, 94);
    pdf.circle(margin + 3, currentY - 1.5, 1.5, 'F');
    pdf.text('ALARP: Risks Reduced ALARP', margin + 8, currentY);
    currentY += PDF_CONFIG.lineHeight + 2;

    // Task-specific WBC3 reference if available
    if (wbc3Ref) {
      pdf.text(`Task-Specific Reference: ${wbc3Ref.reference}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
      if (wbc3Ref.taskTitle) {
        pdf.text(`Task: ${wbc3Ref.taskTitle}`, margin, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    }

    // Supporting guidance
    setFont(pdf, 'small');
    pdf.text('Supporting Guidance: COSWP Chapter 1 (Risk Assessment Methodology)', margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 2;

    // Compliance statement
    const complianceText = 'This risk assessment has been conducted in accordance with Workboat Code Edition 3 (WBC3) Section 3.14 requirements for risk assessment of operations. Risks have been identified and reduced to As Low As Reasonably Practicable (ALARP) in line with WBC3 Appendix 8 Safety Management System requirements.';
    currentY = addWrappedText(pdf, currentY, complianceText, { fontType: 'small' });

    currentY += PDF_CONFIG.sectionSpacing;

    // Signature Blocks (if enabled)
    if (includeSignatures) {
      const pageBreak2 = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: 'Risk Assessment Report (continued)',
        vesselName,
        category
      }), 50);
      currentY = pageBreak2.currentY;

      currentY = addSectionHeading(pdf, currentY, 'Approval & Signatures', 'heading');

      // === SIGNATURES SECTION ===
      const signatureWidth = (contentWidth - 20) / 2;
      const col2X = margin + signatureWidth + 20;

      // Save the starting Y position for BOTH columns
      const signatureStartY = currentY;

      // === Assessed By (Left Column) ===
      let leftY = signatureStartY;

      setFont(pdf, 'subheading');
      pdf.text('ASSESSED BY:', margin, leftY);
      leftY += PDF_CONFIG.lineHeight + 2;

      if (assessment.signatures?.assessor?.data) {
        try {
          pdf.addImage(
            assessment.signatures.assessor.data,
            'PNG',
            margin,
            leftY,
            40,
            12
          );
        } catch (e) {
          console.warn('Could not add assessor signature to PDF', e);
        }
        leftY += 14;

        setFont(pdf, 'body');
        pdf.text(`Name: ${assessment.signatures.assessor.name || '-'}`, margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
        pdf.text(`Position: ${assessment.signatures.assessor.position || '-'}`, margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
        pdf.text(`Date: ${formatDate(assessment.signatures.assessor.timestamp)}`, margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
      } else {
        // Empty signature line
        pdf.line(margin, leftY + 8, margin + signatureWidth - 10, leftY + 8);
        leftY += 12;
        setFont(pdf, 'body');
        pdf.text('Name: _________________', margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
        pdf.text('Position: _________________', margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
        pdf.text('Date: _________________', margin, leftY);
        leftY += PDF_CONFIG.lineHeight;
      }

      // === Approved By (Right Column) ===
      // Use the SAME starting Y position
      let rightY = signatureStartY;

      setFont(pdf, 'subheading');
      pdf.text('APPROVED BY:', col2X, rightY);
      rightY += PDF_CONFIG.lineHeight + 2;

      if (assessment.signatures?.approver?.data) {
        try {
          pdf.addImage(
            assessment.signatures.approver.data,
            'PNG',
            col2X,
            rightY,
            40,
            12
          );
        } catch (e) {
          console.warn('Could not add approver signature to PDF', e);
        }
        rightY += 14;

        setFont(pdf, 'body');
        pdf.text(`Name: ${assessment.signatures.approver.name || '-'}`, col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
        pdf.text(`Position: ${assessment.signatures.approver.position || 'Master'}`, col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
        pdf.text(`Date: ${formatDate(assessment.signatures.approver.timestamp)}`, col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
      } else {
        // Empty signature line
        pdf.line(col2X, rightY + 8, col2X + signatureWidth - 10, rightY + 8);
        rightY += 12;
        setFont(pdf, 'body');
        pdf.text('Name: _________________', col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
        pdf.text('Position: Master', col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
        pdf.text('Date: _________________', col2X, rightY);
        rightY += PDF_CONFIG.lineHeight;
      }

      // Move currentY to whichever column is taller
      currentY = Math.max(leftY, rightY) + 5;
    }

    // Footer
    addReportFooter(pdf, {
      customText: 'WBC3 Compliant Risk Assessment Documentation - FleetSkipper'
    });

    // Save
    const filename = savePDF(pdf, 'risk_assessment', new Date(), assessment.id);

    debug.log('Individual risk assessment PDF exported', {
      filename,
      assessmentId: assessment.id,
      hazardCount: assessment.hazards?.length || 0
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'individual risk assessment export');
    return false;
  }
}

// =============================================================================
// REGISTER EXPORT
// =============================================================================

/**
 * Export risk assessment register as PDF
 * @param {Array} assessments - Array of risk assessments
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportRiskAssessmentRegisterPDF(assessments, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      vesselSettings = {},  // Contains officialNumber, grossTonnage for WBC3 workboats
      companyName = '',
      filterStatus = null,
      filterRiskLevel = null
    } = options;

    if (!assessments || assessments.length === 0) {
      alert('No risk assessments found to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);

    let currentY = margin;

    // Title
    setFont(pdf, 'title');
    pdf.text('RISK ASSESSMENT REGISTER', margin, currentY);
    currentY += 10;

    // === PROFESSIONAL VESSEL HEADER (Classification Society Style) ===
    const headerHeight = 22;

    // Dark header bar (like Lloyd's Register / DNV certificates)
    pdf.setFillColor(30, 41, 59);  // slate-800 - professional dark
    pdf.rect(margin, currentY, contentWidth, headerHeight, 'F');

    // Header text in white
    pdf.setTextColor(255, 255, 255);
    setFont(pdf, 'subheading');

    // Two-row layout: Vessel Name (left) | Company Name (right)
    //                 Off. No | GT (left, second line)
    const headerY1 = currentY + 8;  // First line
    const headerY2 = currentY + 16; // Second line

    // Left: Vessel Name (bold, larger)
    pdf.text(vesselName.toUpperCase(), margin + 5, headerY1);

    // Right: Company Name
    if (companyName) {
      pdf.setFontSize(9);
      const companyWidth = pdf.getTextWidth(companyName.toUpperCase());
      pdf.text(companyName.toUpperCase(), margin + contentWidth - companyWidth - 5, headerY1);
    }

    // Second line: Official Number and GT
    pdf.setFontSize(8);
    pdf.setTextColor(203, 213, 225); // slate-300
    const officialNo = vesselSettings?.officialNumber || '';
    const gtText = vesselSettings?.grossTonnage ? `${vesselSettings.grossTonnage} GT` : '';
    let detailsText = '';
    if (officialNo) detailsText += `Off. No: ${officialNo}`;
    if (officialNo && gtText) detailsText += ' | ';
    if (gtText) detailsText += gtText;
    if (detailsText) {
      pdf.text(detailsText, margin + 5, headerY2);
    }

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    currentY += headerHeight + 4;

    // Report metadata
    setFont(pdf, 'body');
    const reportDate = new Date().toLocaleDateString('en-GB');
    const reportTime = new Date().toLocaleTimeString('en-GB', { hour12: false });
    pdf.text(`Generated: ${reportDate} ${reportTime}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Total Assessments: ${assessments.length}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    // Filter information if applied
    if (filterStatus || filterRiskLevel) {
      pdf.text('Filters Applied:', margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
      if (filterStatus) {
        pdf.text(`  • Status: ${filterStatus}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
      if (filterRiskLevel) {
        pdf.text(`  • Risk Level: ${filterRiskLevel}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    }
    currentY += PDF_CONFIG.sectionSpacing;

    // Statistics
    const stats = calculateRegisterStatistics(assessments);

    currentY = addSectionHeading(pdf, currentY, 'Summary Statistics', 'heading');

    // Risk Level Distribution
    currentY = addSectionHeading(pdf, currentY, 'By Risk Level:', 'subheading');
    setFont(pdf, 'body');

    Object.entries(stats.byRiskLevel).forEach(([level, count]) => {
      if (count > 0) {
        const color = getRiskLevelColor(level);
        pdf.setFillColor(...color);
        pdf.circle(margin + 7, currentY - 1.5, 2, 'F');
        pdf.text(`${RISK_LEVELS[level]?.label || level}: ${count}`, margin + 12, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    });

    currentY += 3;

    // Status Distribution
    currentY = addSectionHeading(pdf, currentY, 'By Status:', 'subheading');
    setFont(pdf, 'body');

    Object.entries(stats.byStatus).forEach(([status, count]) => {
      if (count > 0) {
        const statusLabel = status === 'underReview' ? 'Under Review' :
                           status.charAt(0).toUpperCase() + status.slice(1);
        pdf.text(`  ${statusLabel}: ${count}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    });

    currentY += 3;

    // Review Status
    currentY = addSectionHeading(pdf, currentY, 'Review Status:', 'subheading');
    setFont(pdf, 'body');

    if (stats.overdue > 0) {
      pdf.setTextColor(239, 68, 68);
      pdf.text(`  Overdue: ${stats.overdue}`, margin + 5, currentY);
      pdf.setTextColor(0, 0, 0);
      currentY += PDF_CONFIG.lineHeight;
    }

    if (stats.dueSoon > 0) {
      pdf.setTextColor(234, 179, 8);
      pdf.text(`  Due within 30 days: ${stats.dueSoon}`, margin + 5, currentY);
      pdf.setTextColor(0, 0, 0);
      currentY += PDF_CONFIG.lineHeight;
    }

    pdf.text(`  Average Risk Reduction: ${stats.averageRiskReduction}%`, margin + 5, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Assessment List
    currentY = addSectionHeading(pdf, currentY, 'Assessment Register', 'heading');

    // Table header
    setFont(pdf, 'subheading');
    const col1X = margin;
    const col2X = margin + 70;
    const col3X = margin + 100;
    const col4X = margin + 130;
    const col5X = margin + 155;

    pdf.text('Title', col1X, currentY);
    pdf.text('Status', col2X, currentY);
    pdf.text('Risk', col3X, currentY);
    pdf.text('Review Due', col4X, currentY);
    pdf.text('Hazards', col5X, currentY);
    currentY += PDF_CONFIG.lineHeight;

    // Header line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, margin + contentWidth, currentY);
    currentY += 3;

    // Assessment rows
    assessments.forEach((assessment, index) => {
      const pageBreak = checkPageBreak(pdf, currentY, () => {
        let y = margin;
        y = addReportHeader(pdf, y, {
          title: 'Risk Assessment Register (continued)',
          vesselName,
          category
        });
        return y;
      }, 12);
      currentY = pageBreak.currentY;

      setFont(pdf, 'body');

      // Title (truncate if too long)
      const title = (assessment.title || 'Untitled').substring(0, 35);
      pdf.text(title, col1X, currentY);

      // Status
      const status = STATUS_OPTIONS[assessment.status]?.label || assessment.status || 'Draft';
      pdf.text(status, col2X, currentY);

      // Risk level with color
      const riskLevel = assessment.riskLevel || getRiskLevel(assessment.residualRiskScore || 0);
      const riskScore = assessment.residualRiskScore || 0;
      const riskColor = getRiskLevelColor(riskLevel);
      pdf.setTextColor(...riskColor);
      pdf.text(`${riskLevel.charAt(0).toUpperCase()}${riskLevel.slice(1)} (${riskScore})`, col3X, currentY);
      pdf.setTextColor(0, 0, 0);

      // Review due
      let reviewText = 'Not set';
      if (assessment.nextReviewDate) {
        const isOverdue = new Date(assessment.nextReviewDate) < new Date();
        reviewText = formatDate(assessment.nextReviewDate);
        if (isOverdue) {
          pdf.setTextColor(239, 68, 68);
          reviewText += ' !';
        }
      }
      pdf.text(reviewText, col4X, currentY);
      pdf.setTextColor(0, 0, 0);

      // Hazard count
      const hazardCount = assessment.hazards?.length || 0;
      pdf.text(String(hazardCount), col5X, currentY);

      currentY += PDF_CONFIG.lineHeight;
    });

    currentY += PDF_CONFIG.sectionSpacing;

    // WBC3 Compliance Statement
    const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: 'Risk Assessment Register (continued)',
      vesselName,
      category
    }), 30);
    currentY = pageBreak.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Compliance Statement', 'heading');

    const complianceText = 'This Risk Assessment Register is maintained in accordance with the Workboat Code Edition 3 (WBC3), ISM Code requirements for safety management, and ALARP principles. All assessments include hazard identification, risk evaluation using a 5×5 matrix, and documented control measures following the hierarchy of controls.';
    currentY = addWrappedText(pdf, currentY, complianceText, { fontType: 'body' });

    // Footer
    addReportFooter(pdf, {
      customText: 'WBC3 & ISM Compliant Risk Assessment Register - FleetSkipper'
    });

    // Save
    const filename = savePDF(pdf, 'risk_assessment_register', new Date());

    debug.log('Risk assessment register PDF exported', {
      filename,
      assessmentCount: assessments.length
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'risk assessment register export');
    return false;
  }
}

// =============================================================================
// HIGH RISK REPORT EXPORT
// =============================================================================

/**
 * Export high risk management report as PDF
 * @param {Array} assessments - Array of high risk assessments (score >= 12)
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportHighRiskReportPDF(assessments, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      vesselSettings = {},  // Contains officialNumber, grossTonnage for WBC3 workboats
      companyName = ''
    } = options;

    // Filter to high/extreme only
    const highRiskAssessments = assessments.filter(a => {
      const score = a.residualRiskScore || 0;
      return score >= 12;
    });

    if (highRiskAssessments.length === 0) {
      alert('No high or extreme risk assessments found.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    setFont(pdf, 'title');
    pdf.setTextColor(239, 68, 68);
    pdf.text('HIGH RISK ASSESSMENT REPORT', margin, currentY);
    pdf.setTextColor(0, 0, 0);
    currentY += 10;

    // === PROFESSIONAL VESSEL HEADER (Classification Society Style) ===
    const headerHeight = 22;

    // Dark header bar (like Lloyd's Register / DNV certificates)
    pdf.setFillColor(30, 41, 59);  // slate-800 - professional dark
    pdf.rect(margin, currentY, contentWidth, headerHeight, 'F');

    // Header text in white
    pdf.setTextColor(255, 255, 255);
    setFont(pdf, 'subheading');

    // Two-row layout: Vessel Name (left) | Company Name (right)
    //                 Off. No | GT (left, second line)
    const headerY1 = currentY + 8;  // First line
    const headerY2 = currentY + 16; // Second line

    // Left: Vessel Name (bold, larger)
    pdf.text(vesselName.toUpperCase(), margin + 5, headerY1);

    // Right: Company Name
    if (companyName) {
      pdf.setFontSize(9);
      const companyWidth = pdf.getTextWidth(companyName.toUpperCase());
      pdf.text(companyName.toUpperCase(), margin + contentWidth - companyWidth - 5, headerY1);
    }

    // Second line: Official Number and GT
    pdf.setFontSize(8);
    pdf.setTextColor(203, 213, 225); // slate-300
    const officialNo = vesselSettings?.officialNumber || '';
    const gtText = vesselSettings?.grossTonnage ? `${vesselSettings.grossTonnage} GT` : '';
    let detailsText = '';
    if (officialNo) detailsText += `Off. No: ${officialNo}`;
    if (officialNo && gtText) detailsText += ' | ';
    if (gtText) detailsText += gtText;
    if (detailsText) {
      pdf.text(detailsText, margin + 5, headerY2);
    }

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    currentY += headerHeight + 4;

    // Warning box
    pdf.setFillColor(254, 242, 242); // red-50
    pdf.setDrawColor(239, 68, 68);   // red-500
    pdf.setLineWidth(0.5);
    pdf.rect(margin, currentY, contentWidth, 20, 'FD');

    currentY += 5;
    setFont(pdf, 'subheading');
    pdf.setTextColor(185, 28, 28); // red-700
    pdf.text('MANAGEMENT ATTENTION REQUIRED', margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;

    setFont(pdf, 'body');
    pdf.text(`This report contains ${highRiskAssessments.length} assessment(s) with HIGH or EXTREME residual risk.`, margin + 5, currentY);
    pdf.setTextColor(0, 0, 0);
    currentY += PDF_CONFIG.lineHeight + 10;

    // Report date
    setFont(pdf, 'body');
    pdf.text(`Report Date: ${formatDate(new Date())}`, margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Summary
    const extremeCount = highRiskAssessments.filter(a => (a.residualRiskScore || 0) >= 20).length;
    const highCount = highRiskAssessments.length - extremeCount;

    currentY = addSectionHeading(pdf, currentY, 'Risk Summary', 'heading');

    setFont(pdf, 'body');
    if (extremeCount > 0) {
      const color = getRiskLevelColor('extreme');
      pdf.setFillColor(...color);
      pdf.circle(margin + 7, currentY - 1.5, 2, 'F');
      pdf.text(`Extreme Risk: ${extremeCount}`, margin + 12, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    if (highCount > 0) {
      const color = getRiskLevelColor('high');
      pdf.setFillColor(...color);
      pdf.circle(margin + 7, currentY - 1.5, 2, 'F');
      pdf.text(`High Risk: ${highCount}`, margin + 12, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // Sort by risk score (highest first)
    const sortedAssessments = [...highRiskAssessments].sort((a, b) =>
      (b.residualRiskScore || 0) - (a.residualRiskScore || 0)
    );

    // Extreme risks first
    const extremeRisks = sortedAssessments.filter(a => (a.residualRiskScore || 0) >= 20);
    const highRisks = sortedAssessments.filter(a => {
      const score = a.residualRiskScore || 0;
      return score >= 12 && score < 20;
    });

    // Extreme Risk Section
    if (extremeRisks.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'EXTREME RISK - Immediate Action Required', 'heading');

      extremeRisks.forEach((assessment, index) => {
        const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
          title: 'High Risk Report (continued)',
          vesselName,
          category
        }), 50);
        currentY = pageBreak.currentY;

        currentY = renderHighRiskAssessment(pdf, assessment, currentY, margin, contentWidth, pageWidth);
      });
    }

    // High Risk Section
    if (highRisks.length > 0) {
      const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: 'High Risk Report (continued)',
        vesselName,
        category
      }), 30);
      currentY = pageBreak.currentY;

      currentY = addSectionHeading(pdf, currentY, 'HIGH RISK - Senior Management Attention', 'heading');

      highRisks.forEach((assessment, index) => {
        const pageBreak2 = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
          title: 'High Risk Report (continued)',
          vesselName,
          category
        }), 50);
        currentY = pageBreak2.currentY;

        currentY = renderHighRiskAssessment(pdf, assessment, currentY, margin, contentWidth, pageWidth);
      });
    }

    // Required Actions
    currentY += PDF_CONFIG.sectionSpacing;
    const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: 'High Risk Report (continued)',
      vesselName,
      category
    }), 40);
    currentY = pageBreak.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Required Actions', 'heading');

    setFont(pdf, 'body');
    const actions = [
      'Review all extreme risk assessments immediately',
      'Implement additional controls or cease activity until risk is reduced',
      'Document management decisions and rationale',
      'Schedule follow-up review within 30 days',
      'Brief all affected crew on control measures',
      'Monitor effectiveness of controls'
    ];

    actions.forEach(action => {
      pdf.text(`  ${action}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    });

    // Footer
    addReportFooter(pdf, {
      customText: 'Confidential - Management Attention Required - FleetSkipper'
    });

    // Save
    const filename = savePDF(pdf, 'high_risk_report', new Date());

    debug.log('High risk report PDF exported', {
      filename,
      extremeCount,
      highCount
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'high risk report export');
    return false;
  }
}

/**
 * Render a single high risk assessment in the report
 * @param {jsPDF} pdf - PDF document
 * @param {Object} assessment - Assessment object
 * @param {number} currentY - Current Y position
 * @param {number} margin - Page margin
 * @param {number} contentWidth - Content width
 * @param {number} pageWidth - Page width
 * @returns {number} New Y position
 */
function renderHighRiskAssessment(pdf, assessment, currentY, margin, contentWidth, pageWidth) {
  const riskScore = assessment.residualRiskScore || 0;
  const riskLevel = getRiskLevel(riskScore);
  const alarpZone = getAlarpZone(riskScore);

  // Assessment box
  const boxColor = riskLevel === 'extreme' ? [254, 242, 242] : [255, 247, 237]; // red-50 or orange-50
  const borderColor = riskLevel === 'extreme' ? [239, 68, 68] : [249, 115, 22]; // red-500 or orange-500

  pdf.setFillColor(...boxColor);
  pdf.setDrawColor(...borderColor);
  pdf.setLineWidth(0.3);
  pdf.rect(margin, currentY, contentWidth, 35, 'FD');

  currentY += 5;

  // Title and badge
  setFont(pdf, 'subheading');
  const title = (assessment.title || 'Untitled').substring(0, 50);
  pdf.text(title, margin + 5, currentY);
  addRiskLevelBadge(pdf, pageWidth - margin - 45, currentY - 5, riskLevel, riskScore);
  currentY += PDF_CONFIG.lineHeight + 2;

  setFont(pdf, 'body');
  pdf.text(`ID: ${assessment.id || 'N/A'}`, margin + 5, currentY);
  pdf.text(`ALARP: ${ALARP_ZONES[alarpZone]?.label || 'N/A'}`, margin + 60, currentY);
  currentY += PDF_CONFIG.lineHeight;

  // Highest risk hazard
  if (assessment.hazards && assessment.hazards.length > 0) {
    const highestHazard = assessment.hazards.reduce((max, h) => {
      const score = h.residualRiskScore || (h.residualLikelihood * h.residualSeverity) || 0;
      const maxScore = max.residualRiskScore || (max.residualLikelihood * max.residualSeverity) || 0;
      return score > maxScore ? h : max;
    }, assessment.hazards[0]);

    pdf.text(`Primary Hazard: ${(highestHazard.hazardDescription || 'N/A').substring(0, 60)}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;

    // Critical controls
    if (highestHazard.existingControls || highestHazard.additionalControls) {
      const controls = highestHazard.additionalControls || highestHazard.existingControls;
      pdf.text(`Controls: ${controls.substring(0, 70)}...`, margin + 5, currentY);
    }
  }

  currentY += 10; // Space after box

  return currentY;
}
